import * as React from 'react';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import endOfWeek from 'date-fns/endOfWeek';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfWeek from 'date-fns/startOfWeek';
import isSameMonth from 'date-fns/isSameMonth';

import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import type { StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker';

type CustomPickerDayProps = PickersDayProps<Date> & {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
};

interface DatePickerProps {
  value: Date;
  onChange: (date: Date, startDate: Date, endDate: Date) => void;
  dataTestId?: string;
}

const CustomPickersDay = styled(PickersDay<Date>, {
  shouldForwardProp: (prop) =>
    prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isLastDay && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
}));

const FDatePicker = (props: DatePickerProps) => {
  const { value, onChange, dataTestId = '' } = props;
  const [month, setMonth] = React.useState<Date>(value || new Date());

  const handleChange = (value: Date | null) => {
    if (value === null) {
      return;
    }

    const start = startOfWeek(value);
    const end = endOfWeek(value);
    onChange(value, start, end);
  };

  const RenderWeekPickerDay = (props: PickersDayProps<Date>) => {
    const { day, ...other } = props;

    const start = startOfWeek(value);
    const end = endOfWeek(value);

    const dayIsBetween = isWithinInterval(day, { start, end });
    const isFirstDay = isSameDay(day, start);
    const isLastDay = isSameDay(day, end);

    return (
      <CustomPickersDay
        {...other}
        disableMargin
        outsideCurrentMonth={!isSameMonth(month, day)}
        showDaysOutsideCurrentMonth={true}
        day={day}
        onDaySelect={(newValue: Date) => handleChange(newValue)}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div data-testid={dataTestId}>
        <StaticDatePicker<Date>
          {...({
            displayStaticWrapperAs: 'desktop',
            value,
            onMonthChange: (month: Date) => setMonth(month),
            onYearChange: (month: Date) => setMonth(month),
            onChange: (newValue: Date | null) => handleChange(newValue),
            slots: { day: RenderWeekPickerDay },
            sx: { '&': { borderRadius: '10px' } },
          } satisfies StaticDatePickerProps<Date>)}
        />
      </div>
    </LocalizationProvider>
  );
};

export default FDatePicker;
